import { ArrowRightIcon, Pencil1Icon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { IHospitalDetail } from "../../../../@types/admin/AdminHospitalDetails";
import { IStaffListItem } from "../../../../@types/admin/AdminStaff";
import { IWard } from "../../../../@types/admin/AdminWard";
import { TableSorted } from "../../../../components/TableSorted";
import {
  HOSPITAL_STAFF_QUERY_KEY,
  useHospitalStaff,
} from "../../../../hooks/queries/admin/hospitalDetails";
import { useModal } from "../../../../hooks/useModal";
import { getLongFormattedDate } from "../../../../utils/dateFormatter";
import { RowActions } from "../../Patients/AdminPatients.styles";
import {
  EmailLink,
  ErrorMessage,
  FieldHint,
  FilterButton,
  FiltersActions,
  FiltersContainer,
  RowAction,
  SearchInput,
  SectionCaption,
  SectionCard,
  SectionHeader,
  SectionHeading,
  SectionTitle,
  StaffDetailsPanel,
  StatusBadge,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  useMockPagination,
} from "../../shared";
import { AddStaffModal } from "./AddStaffModal";
import { TabStack } from "./HospitalDetails.styles";

const SEARCH_FIELDS: (keyof IStaffListItem)[] = [
  "email",
  "firstName",
  "lastName",
  "permissionLevel",
];

/**
 * What a staff member's permission level actually reaches.
 *
 * ⚠️ `"hospital"` means ONE WARD here — `cms_hospital` is a ward — and
 * `"hospital-group"` means the whole hospital. Shown raw, as Retool did, the
 * column reads backwards.
 */
function describeReach(permissionLevel: string | undefined): string {
  if (permissionLevel === "hospital") return "One ward";
  if (permissionLevel === "hospital-group") return "Whole hospital";
  if (permissionLevel === "hospital-group-hierarchy") {
    return "Hospital and children";
  }
  return permissionLevel || "-";
}

interface IHospitalStaffTabProps {
  hospital: IHospitalDetail;
  wards: IWard[];
}

/**
 * Staff tab of the Hospital Details page (EN-5175) — Retool's `#### Staffs`
 * container plus its `drawerFrame_StaffDetails` and `drawerFrame_addStaff`.
 *
 * Pure composition on the read side: the details view is the shared
 * `StaffDetailsPanel` (EN-5150), which was built for exactly this. Selecting a
 * row shows it inline rather than in a drawer — the panel embeds two audit
 * trails whose drill-down needs the global modal slot.
 *
 * The add-staff form is new: Retool had one, but it never sent the required
 * `role`, so it fails. See `AddStaffModal`.
 */
export function HospitalStaffTab({ hospital, wards }: IHospitalStaffTabProps) {
  const hospitalId = Number(hospital.id);
  const navigate = useNavigate();
  const { openModal, closeModal } = useModal();
  const [selectedStaffId, setSelectedStaffId] = useState<number | null>(null);

  const { staff, isLoading, isError } = useHospitalStaff(hospitalId);

  const {
    items,
    allItems,
    pagination,
    searchTerm,
    setSearchTerm,
    setPage,
    setPageSize,
  } = useMockPagination({
    data: staff,
    searchFields: SEARCH_FIELDS,
  });

  const columns: ColumnDef<IStaffListItem, string>[] = useMemo(
    () => [
      {
        id: "id",
        header: "Staff ID",
        accessorFn: (row) => String(row.id),
        enableSorting: true,
      },
      {
        id: "name",
        header: "Name",
        accessorFn: (row) =>
          [row.firstName, row.lastName].filter(Boolean).join(" ") || "-",
        enableSorting: true,
      },
      {
        accessorKey: "email",
        header: "Email",
        cell: ({ row }) => (
          <EmailLink
            href={`mailto:${row.original.email}`}
            onClick={(e) => e.stopPropagation()}
          >
            {row.original.email}
          </EmailLink>
        ),
        enableSorting: true,
      },
      {
        // "hospital" means one WARD in this schema, which reads backwards
        // without the translation.
        id: "permissionLevel",
        header: "Reaches",
        accessorFn: (row) => describeReach(row.permissionLevel),
        enableSorting: true,
      },
      {
        id: "isAdmin",
        header: "Admin",
        accessorFn: (row) => (row.isAdmin ? "Yes" : "No"),
        enableSorting: true,
      },
      {
        id: "receiveAlerts",
        header: "Alerts",
        accessorFn: (row) => (row.receiveAlerts ? "Yes" : "No"),
        enableSorting: true,
      },
      {
        id: "activeAccount",
        header: "State",
        accessorFn: (row) => (row.activeAccount ? "Active" : "Inactive"),
        cell: ({ row }) => (
          <StatusBadge
            status={row.original.activeAccount ? "active" : "inactive"}
          >
            {row.original.activeAccount ? "Active" : "Inactive"}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "lastLogin",
        header: "Last login",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.lastLogin, "Never")}</span>
        ),
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions>
            <RowAction
              label="Edit"
              icon={<Pencil1Icon />}
              onClick={() => {
                setSelectedStaffId(row.original.id);
              }}
            />
            <RowAction
              label="Open"
              icon={<ArrowRightIcon />}
              onClick={() => {
                navigate(`/admin/staff/${row.original.id}`);
              }}
            />
          </RowActions>
        ),
      },
    ],
    []
  );

  return (
    <TabStack>
      <SectionCard>
        <SectionHeader>
          <SectionHeading>
            <SectionTitle>Staff</SectionTitle>
            <SectionCaption>
              {staff.length} attached to this hospital or one of its wards
            </SectionCaption>
          </SectionHeading>
        </SectionHeader>

        {wards.length === 0 && (
          <FieldHint>
            This hospital has no wards yet, so a staff member cannot be added —
            the form needs one.
          </FieldHint>
        )}

        <FiltersContainer>
          <SearchInput
            type="text"
            placeholder="Search by name, email, or permission level..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <FiltersActions>
            <FilterButton
              variant="primary"
              disabled={wards.length === 0}
              onClick={() =>
                openModal(
                  <AddStaffModal
                    hospital={hospital}
                    wards={wards}
                    onDone={closeModal}
                  />,
                  {
                    width: "min(640px, 94vw)",
                    maxHeight: "90vh",
                    showCloseButton: true,
                  }
                )
              }
            >
              Add staff
            </FilterButton>
          </FiltersActions>
        </FiltersContainer>

        {isError && (
          <ErrorMessage>
            Failed to load this hospital&apos;s staff. Try refreshing the page.
          </ErrorMessage>
        )}

        <TableContainer>
          <TableWrapper refreshKeys={[[HOSPITAL_STAFF_QUERY_KEY]]}>
            <TableSorted
              columns={columns}
              data={items}
              emptyStateText={
                isLoading ? <TableLoadingState /> : "No staff found"
              }
              onRowClick={(row) => setSelectedStaffId(row.id)}
            />
          </TableWrapper>
          <TablePagination
            pagination={pagination}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            exportCsv={{
              filename: `hospital-${hospitalId}-staff`,
              columns,
              rows: allItems,
            }}
          />
        </TableContainer>
      </SectionCard>

      {selectedStaffId !== null ? (
        <SectionCard>
          <StaffDetailsPanel staffId={selectedStaffId} />
        </SectionCard>
      ) : (
        staff.length > 0 && (
          <SectionCard>
            <FieldHint>
              Select a staff member above to edit their record and account, and
              to see their change history.
            </FieldHint>
          </SectionCard>
        )
      )}
    </TabStack>
  );
}
